// useSessionCost.ts — the running cost of the open conversation, from
// inference's per-session energy accounting.
//
// Keyed on `streaming` so the figure refreshes the moment a turn ends,
// not just on the slow timer — the number moving right after a reply is
// what makes it read as this conversation's cost rather than as a static
// label. The delayed second fetch covers inference's settle lag: it books
// a turn's energy once a power sample covers the instant the response
// finished, which lands a beat after the last token reaches us.
//
// Failures blank the badge instead of surfacing an error. A cost widget is
// not worth interrupting a working conversation for. null means "show
// nothing" — no session selected, or the figure is unknown.
import { useEffect, useState } from 'react';
import { sessionsApi } from '../api';
import type { SessionCost } from '../api';

const SETTLE_MS = 3000;
const REFRESH_MS = 20000;

export function useSessionCost(selectedId: string | null, streaming: boolean): SessionCost | null {
  const [sessionCost, setSessionCost] = useState<SessionCost | null>(null);

  useEffect(() => {
    if (!selectedId) {
      setSessionCost(null);
      return;
    }
    let alive = true;
    const load = () => {
      sessionsApi.cost(selectedId)
        .then((c) => { if (alive) setSessionCost(c); })
        .catch(() => { if (alive) setSessionCost(null); });
    };
    load();
    const settle = setTimeout(load, SETTLE_MS);
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearTimeout(settle);
      clearInterval(timer);
    };
  }, [selectedId, streaming]);

  return sessionCost;
}
